import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Heart, BookOpen, Gem } from 'lucide-react';
import { PiShareFatFill } from 'react-icons/pi';
import { hapticLight, hapticMedium } from '@/lib/haptics';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';

interface AudiobookOptionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  audiobook: {
    id: number;
    title: string;
    coverUrl?: string;
  } | null;
  currentPage: {
    pageNumber: number;
    imageUrl?: string;
    bookTitle: string;
  } | null;
  onSubscribe?: () => void;
}

export function AudiobookOptionsSheet({ 
  open, 
  onOpenChange, 
  audiobook, 
  currentPage,
  onSubscribe 
}: AudiobookOptionsSheetProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: subscriptionData } = useSubscriptionStatus();
  const hasActiveSubscription = subscriptionData?.subscribed;

  // Check if audiobook is liked
  const { data: isLiked = false } = useQuery({
    queryKey: ['liked-audiobook', audiobook?.id, user?.id],
    queryFn: async () => {
      if (!user || !audiobook) return false;
      const { data } = await supabase
        .from('liked_audiobooks')
        .select('id')
        .eq('user_id', user.id)
        .eq('book_id', audiobook.id)
        .maybeSingle();
      return !!data;
    },
    enabled: !!user && !!audiobook,
  });

  // Toggle like mutation
  const toggleLike = useMutation({
    mutationFn: async ({
      bookId,
      userId,
      nextLiked,
    }: {
      bookId: number;
      userId: string;
      nextLiked: boolean;
    }) => {
      if (!userId) throw new Error('Not authenticated');

      if (nextLiked) {
        const { error } = await supabase
          .from('liked_audiobooks')
          .insert({ user_id: userId, book_id: bookId });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('liked_audiobooks')
          .delete()
          .eq('user_id', userId)
          .eq('book_id', bookId);
        if (error) throw error;
      }

      return { bookId, userId, nextLiked };
    },
    onMutate: ({ bookId, userId, nextLiked }) => {
      queryClient.setQueryData(['liked-audiobook', bookId, userId], nextLiked);
    },
    onSuccess: ({ bookId, userId, nextLiked }) => {
      queryClient.invalidateQueries({ queryKey: ['liked-audiobook', bookId, userId] });
      queryClient.invalidateQueries({ queryKey: ['liked-audiobooks'] });
      toast.success(nextLiked ? 'Added to Liked Audiobooks' : 'Removed from Liked Audiobooks');
      hapticMedium();
    },
    onError: (_err, { bookId, userId }) => {
      queryClient.invalidateQueries({ queryKey: ['liked-audiobook', bookId, userId] });
      toast.error('Could not update like');
    },
  });

  const handleShare = async () => {
    hapticLight();
    onOpenChange(false);
    
    const shareUrl = `${window.location.origin}/jukebox?audiobook=${audiobook?.id}&page=${currentPage?.pageNumber || 1}`;
    const shareTitle = currentPage?.bookTitle || 'Audiobook';

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `Listen to ${shareTitle} - Page ${currentPage?.pageNumber || 1} on Jukebox`,
          url: shareUrl,
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied!');
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };

  const handleLike = () => {
    if (!user) {
      toast.error('Sign in to like audiobooks');
      return;
    }
    if (!audiobook) return;

    const nextLiked = !isLiked;
    toggleLike.mutate({ bookId: audiobook.id, userId: user.id, nextLiked });
    onOpenChange(false);
  };

  const handleOpenReader = () => {
    hapticLight();
    onOpenChange(false);
    if (audiobook && currentPage) {
      navigate(`/read/${audiobook.id}?page=${currentPage.pageNumber}`, {
        state: { returnPath: '/jukebox' }
      });
    }
  };

  const handleSubscribe = () => {
    hapticLight();
    onOpenChange(false);
    onSubscribe?.();
  };

  if (!audiobook || !currentPage) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="bg-zinc-900 border-0 rounded-t-3xl p-0 max-h-[85vh] z-[15000]"
      >
        {/* Drag indicator */}
        <div className="flex justify-center pt-3 pb-4">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>

        {/* Audiobook info header */}
        <div className="flex items-center gap-4 px-5 pb-5 border-b border-white/10">
          <img 
            src={currentPage.imageUrl || audiobook.coverUrl} 
            alt={currentPage.bookTitle}
            className="w-14 h-14 rounded-lg object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold truncate">Page {currentPage.pageNumber}</p>
            <p className="text-white/60 text-sm truncate">
              {currentPage.bookTitle}
            </p>
          </div>
        </div>

        {/* Menu options */}
        <div className="py-2">
          {/* Share */}
          <button 
            onClick={handleShare}
            className="w-full flex items-center gap-4 px-5 py-4 active:bg-white/5 transition-colors"
          >
            <PiShareFatFill className="w-6 h-6 text-white/70" />
            <span className="text-white text-base">Share</span>
          </button>

          {/* Like */}
          <button 
            onClick={handleLike}
            className="w-full flex items-center gap-4 px-5 py-4 active:bg-white/5 transition-colors"
          >
            <Heart 
              className={isLiked ? 'w-6 h-6 text-primary' : 'w-6 h-6 text-white/70'}
              fill={isLiked ? 'currentColor' : 'none'}
            />
            <span className="text-white text-base">
              {isLiked ? 'Remove from Liked' : 'Add to Liked'}
            </span>
          </button>

          {/* Open in Reader */}
          <button 
            onClick={handleOpenReader}
            className="w-full flex items-center gap-4 px-5 py-4 active:bg-white/5 transition-colors"
          >
            <BookOpen className="w-6 h-6 text-white/70" />
            <span className="text-white text-base">Open in Reader</span>
          </button>

          {/* Subscribe - only if not subscribed */}
          {!hasActiveSubscription && (
            <button 
              onClick={handleSubscribe}
              className="w-full flex items-center justify-between px-5 py-4 active:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-4">
                <Gem className="w-6 h-6 text-white/70" />
                <span className="text-white text-base">Unlock all content</span>
              </div>
              <span className="text-emerald-400 font-semibold text-sm">Premium</span>
            </button>
          )}
        </div>

        {/* Safe area padding */}
        <div style={{ paddingBottom: 'env(safe-area-inset-bottom, 1rem)' }} />
      </SheetContent>
    </Sheet>
  );
}
